// src/components/map/BaseMap.tsx
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { GoogleMap, GoogleMapProps } from '@react-google-maps/api';
import { useGoogleMapContext } from './GoogleMapProvider';
import { Loader2 } from 'lucide-react';

export interface MapPosition {
  lat: number;
  lng: number;
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface BaseMapProps {
  center?: MapPosition;
  zoom?: number;
  height?: string | number;
  width?: string | number;
  onClick?: (position: MapPosition) => void;
  onBoundsChanged?: (bounds: MapBounds) => void;
  onZoomChanged?: (zoom: number) => void;
  onMapLoad?: (map: google.maps.Map) => void;
  children?: React.ReactNode;
  options?: google.maps.MapOptions;
  className?: string;
}

const defaultCenter: MapPosition = {
  lat: 39.8283, // Center of continental US
  lng: -98.5795,
};

const defaultOptions: google.maps.MapOptions = {
  gestureHandling: 'greedy', // Captures all touch gestures - prevents page zoom on mobile
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ],
};

const BaseMap: React.FC<BaseMapProps> = ({
  center = defaultCenter,
  zoom = 10,
  height = '100%',
  width = '100%',
  onClick,
  onBoundsChanged,
  onZoomChanged,
  onMapLoad,
  children,
  options = {},
  className = '',
}) => {
  const { isLoaded } = useGoogleMapContext();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [currentZoom, setCurrentZoom] = useState(zoom);

  const containerStyle = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  const handleLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    onMapLoad?.(map);
  }, [onMapLoad]);

  const handleClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng && onClick) {
      onClick({
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      });
    }
  }, [onClick]);

  const handleBoundsChanged = useCallback(() => {
    if (mapRef.current && onBoundsChanged) {
      const bounds = mapRef.current.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        onBoundsChanged({
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng(),
        });
      }
    }
  }, [onBoundsChanged]);

  const handleZoomChanged = useCallback(() => {
    if (mapRef.current) {
      const newZoom = mapRef.current.getZoom();
      if (newZoom !== undefined && newZoom !== currentZoom) {
        setCurrentZoom(newZoom);
        onZoomChanged?.(newZoom);
      }
    }
  }, [currentZoom, onZoomChanged]);

  if (!isLoaded) {
    return (
      <div
        style={containerStyle}
        className={`flex items-center justify-center bg-gray-100 ${className}`}
      >
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Loading map...</span>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      mapContainerClassName={className}
      center={center}
      zoom={zoom}
      onLoad={handleLoad}
      onClick={handleClick}
      onBoundsChanged={handleBoundsChanged}
      onZoomChanged={handleZoomChanged}
      options={{ ...defaultOptions, ...options }}
    >
      {children}
    </GoogleMap>
  );
};

export default BaseMap;
